import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { gtplSaathiAPI } from '../services/api';
import toast from 'react-hot-toast';
import { RefreshCw, Bot, User, ScrollText, Search, Inbox } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import { Screen, PageHead, Notice, Stats, Stat, Item, Pill, Empty, Loading, Pager } from '../components/gtpl/GtplKit';

const ACTION_LABEL = {
  suspend: 'Suspend', activate: 'Activate', renew: 'Renew', order: 'Place Order',
  multiRenew: 'Multi-Renew', modify: 'Modify Profile', hardwareReplacement: 'Hardware Replacement',
};

function fmtDateTime(d) {
  return d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
}

export default function GtplActionLogPage() {
  useDarkReady();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [successCounts, setSuccessCounts] = useState({});
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [action, setAction] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await gtplSaathiAPI.getActionLogs({
        page, limit: 25, action: action || undefined, success: success || undefined, search: search || undefined,
      });
      setLogs(data.data || []);
      setPagination(data.pagination || { total: 0, page: 1, pages: 1 });
      setSuccessCounts(data.successCounts || {});
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load GTPL Saathi logs');
    } finally {
      setLoading(false);
    }
  }, [page, action, success, search]);

  useEffect(() => { load(); }, [load]);

  function changeAction(next) { setAction(next); setPage(1); }
  function changeSuccess(next) { setSuccess(next); setPage(1); }
  function submitSearch(e) { e.preventDefault(); setSearch(searchInput.trim()); setPage(1); }

  return (
    <Screen>
      <PageHead icon={ScrollText} tone="info" title="GTPL logs" sub="Every order or change sent to GTPL, who did it, and whether it worked.">
        <button type="button" className="btn btn-outline" onClick={load} disabled={loading}>
          <RefreshCw size={15} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} /> Refresh
        </button>
      </PageHead>

      <Notice tone="info">
        Covers renew, place order, suspend / activate, multi-renew, modify and box replacement, whether a staff member
        clicked it or it fired automatically from a payment. Unlike the <Link to="/gtpl-saathi-retry-queue">Retry Queue</Link>,
        this also shows real failures (for example, not enough GTPL wallet balance) that are never queued for retry.
      </Notice>

      <Stats>
        <Stat tone="ok" value={successCounts.success ?? 0} label="Succeeded" active={success === 'true'} onClick={() => changeSuccess(success === 'true' ? '' : 'true')} />
        <Stat tone="bad" value={successCounts.failed ?? 0} label="Failed" active={success === 'false'} onClick={() => changeSuccess(success === 'false' ? '' : 'false')} />
      </Stats>

      <form onSubmit={submitSearch} className="gx-actions" style={{ marginBottom: 10 }}>
        <input className="form-control" style={{ flex: '1 1 220px' }} placeholder="Search customer, STB, account…"
          value={searchInput} onChange={e => setSearchInput(e.target.value)} />
        <button type="submit" className="btn btn-primary"><Search size={15} /> Search</button>
        <select className="form-control" style={{ flex: '0 1 220px' }} value={action} onChange={e => changeAction(e.target.value)} aria-label="Action">
          <option value="">All actions</option>
          {Object.entries(ACTION_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </form>

      {loading ? <Loading /> : logs.length === 0 ? (
        <Empty icon={Inbox} title="Nothing here">No log entries match.</Empty>
      ) : (
        <div className="gx-list">
          {logs.map((l) => {
            const customer = l.customer;
            const custLabel = customer
              ? `${customer.name}${customer.customerId ? ` (${customer.customerId})` : ''}`
              : (l.customerName ? `${l.customerName}${l.customerLabel ? ` (${l.customerLabel})` : ''}` : '—');
            return (
              <Item key={l._id} tone={l.success ? 'ok' : 'bad'}
                title={ACTION_LABEL[l.action] || l.action}
                sub={customer ? <Link to={`/customers/${customer._id}`}>{custLabel}</Link> : custLabel}
                right={<Pill tone={l.success ? 'ok' : 'bad'} dot>{l.success ? 'Succeeded' : 'Failed'}</Pill>}
                meta={<>
                  <span>{l.trigger === 'auto' ? <Bot size={12} style={{ verticalAlign: -2 }} /> : <User size={12} style={{ verticalAlign: -2 }} />} {l.trigger === 'auto' ? 'Automatic' : 'Manual'}</span>
                  {(l.stb || l.accountNumber) && <span style={{ fontFamily: 'monospace' }}>{l.stb || l.accountNumber}</span>}
                  <span>{fmtDateTime(l.createdAt)}{l.performedBy ? ` · by ${l.performedBy}` : ''}</span>
                </>}
                actions={l.message ? <div className="gx-hint" style={{ margin: 0 }}>{l.message}</div> : null}
              />
            );
          })}
        </div>
      )}

      <Pager page={pagination.page} pages={pagination.pages} total={pagination.total} onPage={setPage} />
    </Screen>
  );
}
